import { decodeSafeEntities } from '@/lib/sanitise-strings'
import Link from 'next/link'
import AppImage from '@/components/AppImage'
import { ImageFieldProps, LinkProps } from '@/types/types'

export type PromoBlockProps = {
  image?: ImageFieldProps
  link?: LinkProps
  heading?: string
  title?: string
  text?: string
  isLoading?: boolean
}

const themeClasses = `
    theme--base-0:theme--primary-50
    theme--primary-50:theme--base-0
    theme--gradient-light:theme--base-0
    bg-theme 
    text-fg
    `

export default function PromoBlock({
  image,
  link,
  heading,
  title,
  text,
  isLoading = false,
}: PromoBlockProps) {
  if (isLoading) {
    return <PromoBlockSkeleton />
  }

  // Required fields
  if (!title) {
    return null
  }

  const transforms = {
    dpr: '2',
    width: '396',
    height: '240',
  }

  // Mobile slider images
  const pictureSources = {
    // Desktop
    '(min-width: 1520px)': { width: 396, height: 240 },
    '(max-width: 1519px)': { width: 455, height: 270 },
    '(max-width: 989px)': { width: 350, height: 213 },

    // Full width mobile
    '(max-width: 769px)': { width: 714, height: 405 },
    '(max-width: 599px)': { width: 544, height: 309 },
    '(max-width: 399px)': { width: 352, height: 200 },
  }

  const { url: imageUrl, alt: imageAlt } = image || {}

  const imageHtml = imageUrl ? (
    <AppImage
      path={imageUrl}
      alt={imageAlt}
      height={Number(transforms.height)}
      width={Number(transforms.width)}
      loading="lazy"
      className="w-full s-up:h-full s-up:object-cover"
      pictureClassName="w-full"
      transforms={transforms}
      // Picture source transforms
      pictureSources={pictureSources}
    />
  ) : null

  const { url: linkUrl, target: linkTarget = '', title: linkTitle = 'Read more' } = link || {}

  return (
    <div className="component component--promo-block component--snug">
      <div className="container">
        <div
          className={`${themeClasses} flex overflow-hidden rounded-lg text-fg m:p-m s:flex-col s:gap-s s-up:items-center s-up:gap-l m-up:px-xl m-up:py-l`}>
          {/* TODO: Add option for icon instead of image */}

          {imageHtml && (
            <div
              className={`relative flex flex-shrink-0 self-stretch overflow-hidden s:min-w-full xs-up:rounded-sm s-up:w-[min(240px,40%)] m-up:w-[min(320px,20%)] ${link ? 'cursor-pointer' : ''} xs:-mx-m xs:-mt-m`}>
              {link ? (
                <Link href={linkUrl} target={linkTarget} className="h-full w-full">
                  {imageHtml}
                </Link>
              ) : (
                imageHtml
              )}
            </div>
          )}

          <div className="flex w-full m:flex-col m:gap-m m-up:items-center m-up:justify-between">
            <div className="flex flex-col gap-xs">
              <div className="flex flex-col items-start gap-xxxs">
                {heading ? <div className="p--small text-body mb-0 block">{heading}</div> : null}

                {linkUrl ? (
                  <Link href={linkUrl} target={linkTarget} className="h4 mb-0">
                    {decodeSafeEntities(title)}
                  </Link>
                ) : (
                  <span className="h4 mb-0">{decodeSafeEntities(title)}</span>
                )}
              </div>

              {text ? <div className="p" dangerouslySetInnerHTML={{ __html: text }}></div> : null}
            </div>

            {linkUrl ? (
              <div className="flex flex-shrink-0 m-up:w-[340px] m-up:justify-center">
                <Link href={linkUrl} target={linkTarget} className="btn btn--solid">
                  {decodeSafeEntities(linkTitle)}
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

function PromoBlockSkeleton() {
  const skeletonClass = `
    bg-theme-alt
    animate-pulse
    w-full
  `

  return (
    <div className="component component--featured-post">
      <div className="container">
        <div
          className={`${themeClasses} flex overflow-hidden rounded-lg text-fg m:p-m s:flex-col s:gap-s s-up:items-center s-up:gap-l m-up:px-xl m-up:py-l`}>
          <div className="flex w-full m:flex-col m:gap-m m-up:items-center m-up:justify-between">
            <div className="flex w-full flex-col gap-xs">
              <div className="flex max-w-[400px] flex-col items-start gap-xxxs">
                <span className={`${skeletonClass} h4 mb-0`}>&nbsp;</span>
              </div>

              <div className={`${skeletonClass} p`}>
                <br />
                <br />
                <br />
              </div>
            </div>

            <div className="flex flex-shrink-0 m-up:w-[340px] m-up:justify-center">
              <div className={`${skeletonClass} flex h-[52]`}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
